import { useEffect, useState } from 'react';
import { resolveAppleMusicSongUrl, trackLinkKey } from '../api/itunesClient';

// Resolves canonical Apple Music URLs for a tracklist in the background so
// the track anchors can carry them as plain hrefs — a real universal link
// opens the native Music app directly from the tap, with no intermediate
// tab. Returns a Map of trackLinkKey(track) -> url that fills in as results
// arrive; unresolved tracks keep their search-URL fallback.
//
// Lookups run one at a time, in list order: each track costs at most one
// upstream iTunes call ever (shared server cache), and serializing means a
// long playlist can't flood the server's rate-limit queue — the top of the
// list, which users click first, resolves first. Does nothing unless the
// service is apple-music.
export function useAppleMusicSongLinks(tracks, musicService) {
  const [links, setLinks] = useState(() => new Map());

  useEffect(() => {
    if (musicService !== 'apple-music' || !tracks || tracks.length === 0) return;
    let cancelled = false;

    (async () => {
      for (const track of tracks) {
        if (cancelled) return;
        const url = await resolveAppleMusicSongUrl(track);
        if (cancelled) return;
        if (url) {
          const key = trackLinkKey(track);
          setLinks((prev) => {
            if (prev.get(key) === url) return prev;
            const next = new Map(prev);
            next.set(key, url);
            return next;
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tracks, musicService]);

  return links;
}
